import { useEffect, useCallback } from 'react';
import { BrowserRouter, Routes, Route, useSearchParams, useNavigate } from 'react-router-dom';
import '@/App.css';

import useAuthStore from '@/stores/authStore';
import useThemeStore from '@/stores/themeStore';

import { Toaster } from '@/components/ui/sonner';
import AuthGate from '@/components/auth/AuthGate';
import AppLayout from '@/components/layout/AppLayout';

import Dashboard from '@/pages/Dashboard';
import InterviewsPage from '@/pages/InterviewsPage';
import InterviewSetup from '@/pages/InterviewSetup';
import InterviewRoom from '@/pages/InterviewRoom';
import ReportScreen from '@/pages/ReportScreen';
import ProgressPage from '@/pages/ProgressPage';
import KnowledgePage from '@/pages/KnowledgePage';
import LeaderboardPage from '@/pages/LeaderboardPage';
import SentimentPage from '@/pages/SentimentPage';
import SettingsPage from '@/pages/SettingsPage';

import { TEST_IDS } from '@/constants/testIds';

/**
 * Handles the token query-param exchange on initial load.
 */
function TokenExchanger() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { exchangeToken, checkSession, user, loading } = useAuthStore();
  const navigate = useNavigate();

  const handleAuth = useCallback(async () => {
    const token = searchParams.get('token');
    const assessmentId = searchParams.get('assessmentId');
    if (token) {
      // Exchange token for session cookie
      const result = await exchangeToken(token);
      // Remove token (and assessmentId) from URL after exchange
      searchParams.delete('token');
      searchParams.delete('assessmentId');
      setSearchParams(searchParams, { replace: true });
      if (result) {
        if (assessmentId) {
          // Launched from Taskverse's "My Interviews" for a specific assessment —
          // stash it for InterviewSetup to pick up (survives the URL cleanup above).
          sessionStorage.setItem('botview_assessment_id', assessmentId);
          navigate('/interview/setup', { replace: true });
        } else {
          navigate('/', { replace: true });
        }
      }
    } else {
      // No token in URL — try to restore from session cookie
      await checkSession();
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    handleAuth();
  }, [handleAuth]);

  // Loading state
  if (loading) {
    return (
      <div
        data-testid={TEST_IDS.LOADING_SPINNER}
        className="min-h-screen flex items-center justify-center bg-background"
      >
        <div className="flex flex-col items-center gap-4 animate-fade-in">
          <div
            className="w-10 h-10 rounded-xl animate-pulse"
            style={{ background: 'linear-gradient(180deg, #FFC233 0%, #FF6B1A 100%)' }}
          />
          <p className="text-sm text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  // Not authenticated — show auth gate
  if (!user) {
    return <AuthGate />;
  }

  // Authenticated — show app
  return (
    <Routes>
      {/* Interview room has its own full-screen dark layout */}
      <Route path="/interview/setup" element={
        <AppLayout><InterviewSetup /></AppLayout>
      } />
      <Route path="/interview/:sessionId" element={<InterviewRoom />} />
      {/* All other pages use the standard AppLayout */}
      <Route path="*" element={
        <AppLayout>
          <Routes>
            <Route path="/" element={<Dashboard />} />
            <Route path="/interviews" element={<InterviewsPage />} />
            <Route path="/progress" element={<ProgressPage />} />
            <Route path="/knowledge" element={<KnowledgePage />} />
            <Route path="/leaderboard" element={<LeaderboardPage />} />
            <Route path="/sentiment" element={<SentimentPage />} />
            <Route path="/settings" element={<SettingsPage />} />
            <Route path="/report/:sessionId" element={<ReportScreen />} />
          </Routes>
        </AppLayout>
      } />
    </Routes>
  );
}

function App() {
  const { init } = useThemeStore();

  useEffect(() => {
    init();
  }, [init]);

  return (
    <BrowserRouter>
      <Toaster position="top-center" richColors />
      <Routes>
        <Route path="/*" element={<TokenExchanger />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
